import React, { useCallback, useEffect, useState } from 'react';
import { Button, Callout, useStateInRef } from 'seagull';
import PaymentMethodPanel from "./PaymentMethodPanel";
import { useVerifyPlaidBankAccount } from "../../hooks/useVerifyPlaidBankAccount";
import { PlaidBankAccountStatusTypes } from "../../models/payment-method";
import ExternalLinkButton from "../ExternalLinkButton";
import { getHelpArticle } from "../../infra/utils";
import { useStoreActions } from "../../contexts";
import { useReloginPlaidBankAccount } from "../../hooks/useReloginPlaidBankAccount";
import { paymentMethodModel } from "../../models";
function ManualVerificationCallout(props) {
  const {
    intent,
    hasCompleteVerificationAction,
    hasPermissionToVerify,
    text,
    id,
    verifyable,
    setNewVerificationStatus
  } = props;
  const verifyPlaidBankAccount = useVerifyPlaidBankAccount();
  return /*#__PURE__*/React.createElement(Callout, {
    variant: "paragraph2",
    weight: "light",
    intent: intent,
    "data-testid": "payment-method-callout",
    withAction: hasCompleteVerificationAction && verifyable && /*#__PURE__*/React.createElement(Button, {
      size: "small",
      skin: "darkSecondary",
      "data-testid": 'payment-method-complete-verification',
      disabled: !hasPermissionToVerify,
      onClick: () => {
        verifyPlaidBankAccount({
          fundingSourceId: id,
          onVerify() {
            // Optimistic UI update.
            setNewVerificationStatus(PlaidBankAccountStatusTypes.ManuallyVerified);
          },
          onVerifyFailed() {
            // Optimistic UI update.
            setNewVerificationStatus(PlaidBankAccountStatusTypes.VerificationFailed);
          }
        });
      }
    }, "Complete verification")
  }, text);
}
function AutomaticVerificationCallout(props) {
  const {
    intent,
    hasPermissionToVerify,
    text,
    id,
    verificationStatus,
    verifyable,
    isVendor
  } = props;
  const getProps = useStateInRef(props);
  const {
    api
  } = useStoreActions();
  const [loginRequired, setLoginRequired] = useState(false);
  const reloginPlaidBankAccount = useReloginPlaidBankAccount();
  const queryIsLoginRequired = useCallback(() => api.plaid.isLoginRequired(getProps().id).then(setLoginRequired), []);
  useEffect(() => {
    if (verifyable && verificationStatus === PlaidBankAccountStatusTypes.PendingAutomaticVerification) {
      queryIsLoginRequired();
    }
  }, [verificationStatus, verifyable, queryIsLoginRequired]);
  return /*#__PURE__*/React.createElement(Callout, {
    variant: "paragraph2",
    weight: "light",
    intent: intent,
    "data-testid": "payment-method-callout",
    withAction: loginRequired && verifyable && /*#__PURE__*/React.createElement(Button, {
      size: "small",
      skin: "darkSecondary",
      "data-testid": 'payment-method-reconnect',
      disabled: !hasPermissionToVerify || isVendor,
      onClick: () => {
        reloginPlaidBankAccount({
          fundingSourceId: id,
          onRelogin() {
            api.plaid.loginRepaired(id).then(queryIsLoginRequired);
          }
        });
      }
    }, loginRequired ? 'Reconnect' : 'Complete verification')
  }, loginRequired ? 'Re-authentication required due to bank or account updates.' : text);
}
function GenericWarningCallout(props) {
  const {
    text,
    intent
  } = props;
  return /*#__PURE__*/React.createElement(Callout, {
    variant: "paragraph2",
    weight: "light",
    intent: intent,
    "data-testid": "payment-method-callout"
  }, text);
}
function getCallout(bankAccount, verificationStatus, isProposal, hasPermissionsToVerify, verifyable, isVendor, setNewVerificationStatus) {
  if (paymentMethodModel.isErroredBankAccount(bankAccount.bankAccount)) {
    return /*#__PURE__*/React.createElement(GenericWarningCallout, {
      intent: "error",
      text: bankAccount.bankAccount.errorMessage
    });
  } else if (paymentMethodModel.isVerifyWithStripe(bankAccount)) {
    return /*#__PURE__*/React.createElement(GenericWarningCallout, {
      intent: "warning",
      text: "Verification failed. Please remove and retry or add a different payment method."
    });
  } else if (paymentMethodModel.isFailedDueToDuplicate(bankAccount)) {
    return /*#__PURE__*/React.createElement(GenericWarningCallout, {
      intent: "warning",
      text: "Bank account already connected and can't be selected again. Remove this entry via the three dots menu."
    });
  } else {
    switch (verificationStatus) {
      case PlaidBankAccountStatusTypes.PendingAutomaticVerification:
        return /*#__PURE__*/React.createElement(AutomaticVerificationCallout, {
          intent: isProposal ? 'info' : 'warning',
          text: isProposal ? 'Your bank account will be automatically verified within one business day.' : 'Automatic verification in progress. This process takes 1-2 business days.',
          hasPermissionToVerify: hasPermissionsToVerify,
          id: bankAccount.id,
          verificationStatus: verificationStatus,
          verifyable: verifyable && !isProposal,
          isVendor: isVendor,
          setNewVerificationStatus: setNewVerificationStatus
        });
      case PlaidBankAccountStatusTypes.PendingManualVerification:
        return /*#__PURE__*/React.createElement(ManualVerificationCallout, {
          intent: isProposal ? 'info' : 'warning',
          text: isProposal ? 'Verification process initiated. It is required to approve the agreement to complete the verification process.' : 'Pending verification',
          hasCompleteVerificationAction: !isProposal,
          hasPermissionToVerify: hasPermissionsToVerify,
          id: bankAccount.id,
          verifyable: verifyable,
          setNewVerificationStatus: setNewVerificationStatus
        });
      case PlaidBankAccountStatusTypes.VerificationFailed:
      case PlaidBankAccountStatusTypes.VerificationExpired:
        return /*#__PURE__*/React.createElement(GenericWarningCallout, {
          intent: "warning",
          text: "Verification failed. Please remove and retry or add a different payment method."
        });
      default:
        return null;
    }
  }
}
export default function BankAccountPanel(props) {
  const {
    bankAccount,
    isProposal,
    apiGatewayHost,
    isSelected,
    isMobile,
    removable,
    selectable,
    verifyable,
    hasPermissionToVerify = false,
    isVendor,
    onSelect,
    onRemove
  } = props;
  const [newVerificationStatus, setNewVerificationStatus] = useState(null);
  const callout = getCallout(bankAccount, newVerificationStatus || paymentMethodModel.verificationStatus(bankAccount), isProposal, hasPermissionToVerify, !!verifyable, isVendor, setNewVerificationStatus);
  return /*#__PURE__*/React.createElement(PaymentMethodPanel, {
    removable: removable,
    selectable: selectable,
    isMobile: isMobile,
    isSelected: isSelected,
    onSelect: onSelect,
    onRemove: onRemove,
    title: "Bank Account",
    description: "Account number: ****".concat(paymentMethodModel.last4(bankAccount)),
    logo: /*#__PURE__*/React.createElement("img", {
      "data-testid": "bank-account-logo",
      alt: "bank account logo",
      src: "".concat(apiGatewayHost, "/institution/").concat(paymentMethodModel.institutionId(bankAccount) || 'default', "/logo.png")
    }),
    footer: callout != null ? callout : isSelected && !isVendor ? /*#__PURE__*/React.createElement(Callout, {
      "data-testid": "company-ids-callout",
      variant: "paragraph2",
      weight: "light",
      intent: "info"
    }, "To ensure ACH transactions are processed smoothly, please ask your bank to whitelist these", ' ', /*#__PURE__*/React.createElement(ExternalLinkButton, {
      openInNewTab: true,
      variant: "paragraph2",
      href: getHelpArticle('6668991-can-i-minimize-the-potential-for-disputes-and-declined-payments-due-to-unauthorization-failures-ach')
    }, "company IDs"), ".") : null
  });
}